<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading">
    <div class="header-section">
      <div class="step-label">{{ $t('bulk_skip_tracking') }} - {{ $t('Use RFID') }}</div>
    </div>

    <div class="barcode-number-block">
      <span>
        {{ $t('Number of Scanned Barcodes') }}
      </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodeList.length }}
        {{ bulkUpdateOperationLimitCount ? `/${bulkUpdateOperationLimitCount?.maxValue}` : '' }}
      </span>
    </div>

    <div class="barcode-list-container">
      <div v-if="scannedBarcodeList.length === 0" class="empty-state">
        <IconRfid class="empty-icon" />
        <p>{{ $t('Scan RFID tags to add barcodes') }}</p>
      </div>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList" />
    </div>

    <div class="button-container">
      <el-button class="back-button" @click="goBack">
        {{ $t('Back') }}
      </el-button>
      <el-button
        type="primary"
        class="submit-button"
        :disabled="
          scannedBarcodeList.length < (bulkUpdateOperationLimitCount ? bulkUpdateOperationLimitCount?.minValue : 1)
        "
        @click="submit()"
      >
        {{ $t('OK') }}
      </el-button>
    </div>
  </div>

  <el-dialog
    center
    :lock-scroll="true"
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    :title="checkScannedBarcode ? $t('Scanned Barcodes') : ''"
  >
    <template v-if="checkScannedBarcode">
      <p v-if="scannedBarcodeList.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList || []" />
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="onCloseDialog">
          {{ $t('Done') }}
        </el-button>
      </span>
    </template>
  </el-dialog>

  <RfidItemSelector v-model:is-show="showRfidScanDialog" mode="multiple-select" @confirm="onRfidScanConfirmed" />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import IconRfid from '@/components/svg/IconRfid.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import RfidItemSelector from '@/views/bizBrowser/RfidItemSelector.vue'
import { ADD_TRACKING, FETCH_BARCODE, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { IBarcode, IProject, ITrackingRequestBody, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { tracking, getBarcode } from '@/utils/api'
import { cloneDeep } from 'lodash'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'

@Options({
  components: {
    BarcodeLabelList,
    IconRfid,
    IconArrowBack,
    RfidItemSelector,
  },
  name: 'BulkSkipTrackingRfid',
})
export default class BulkSkipTrackingRfid extends Vue {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  requestBody = {} as ITrackingRequestBody
  nextSelectTrackingPointName = ''
  scannedBarcodeList: IBarcode[] = []
  showRfidScanDialog = true

  get barcode(): IBarcode {
    return this.$store.state.barcode.barcode
  }

  async created() {
    if (!this.$store.state.barcode.barcode) {
      await this.$store.dispatch(FETCH_BARCODE, {
        id: this.$route.params.barcodeId,
      })
    }
    this.nextSelectTrackingPointName = this.$route.params.nextTrackName as string
    this.requestBody = JSON.parse(localStorage.tempRequestForMultipleBarcodeScan)
    this.scannedBarcodeList.push(cloneDeep(this.barcode))
  }

  goBack() {
    this.$router.back()
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (to.name === 'scanned-barcode-list') {
      next()
      return
    }

    if (this.scannedBarcodeList.length > 1) {
      try {
        await this.$confirm(this.$t('confirm_rfid_scan_result_is_not_saved_when_leaving_page'), this.$t('warning'), {
          confirmButtonText: this.$t('ok'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        })
        next()
      } catch {
        next(false)
      }
    } else {
      next()
    }
  }

  get bulkUpdateOperationLimitCount() {
    const project = this.$store.state.project.details as IProject
    const projectBarcode = project.barcodeTypes?.[this.$store.getters.barcodeType]
    return projectBarcode?.bulkUpdateOperationLimitCount
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  async validateBarcode(resultScanned: IBarcode) {
    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) {
      throw this.$t('scanned_barcode_not_same_project')
    }

    const { barcodeType } = resultScanned
    if (!barcodeType) {
      throw this.$t('barcode not activated')
    }

    const data = {
      barcodeId: this.barcode?.id,
      barcodeIds: [resultScanned?.id],
      trackingData: this.requestBody.trackingData,
      selectedNextTrackpoint: this.nextSelectTrackingPointName,
      isSkip: true,
      isDryRun: true,
    }

    await tracking(data)
  }

  async onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    try {
      const projectCode = this.$route?.params?.project?.toString().split('@')[0] || ''

      for (const rfidResult of rfidList) {
        try {
          const barcodeId = `projectCode=${projectCode}`
          const scannedBarcode = await getBarcode(barcodeId, rfidResult.parsedValue)

          const isAlreadyAdded = this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedBarcode.id)
          if (isAlreadyAdded) {
            continue
          }

          await this.validateBarcode(scannedBarcode)
          this.scannedBarcodeList.push(scannedBarcode)
          openMessage(this.$t('barcode detected'), 'success')
        } catch (error) {
          if (error !== 'cancel') {
            errorHandler(error as TError)
          }
        }
      }
    } finally {
      this.loading = false
    }
  }

  async submit() {
    const barcodes = this.scannedBarcodeList.filter((barcode: IBarcode) => barcode.id !== this.barcode.id)
    this.$store.dispatch(SAVE_SCANNED_BARCODES, barcodes)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('bulk_skip_tracking_read_result'),
        operation: ADD_TRACKING,
        extParams: JSON.stringify({
          trackingData: this.requestBody.trackingData,
          selectedNextTrackpoint: this.nextSelectTrackingPointName,
          isSkip: true,
        }),
      },
    })
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.header-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 25px;

  .back-button {
    background-color: #525252;
    flex-shrink: 0;
    padding: 8px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .step-label {
    flex: 1;
    margin-bottom: 0;
  }
}

.step-label {
  margin-bottom: 25px;
}

.barcode-number-block {
  margin-top: 20px;
  margin-bottom: 20px;
}

.barcode-list-container {
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 20px;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  :deep(.back-button),
  :deep(.submit-button) {
    flex: 1 1 50%;
    min-width: 0;
    width: 50%;
    max-width: 50%;
    height: 40px !important;
    padding: 12px 20px !important;
    font-size: 14px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    margin: 0 !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;

  .empty-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
  }
}
</style>
